import type React from "react";
import { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { fetchFiltersDepartments } from "../../features/filters/filterSlice";
import {
    createCompanyMember,
    updateCompanyMember,
    fetchCompanyMemberById,
    fetchMemberRoles,
    type MemberRoleOption,
    type CreateCompanyMemberPayload,
    type UpdateCompanyMemberPayload
} from "../../features/companyMembers/companyMemberAPI";
import { fetchCompanyMember } from "../../features/auth/authAPI";
import { ensureCurrentUser } from "../../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import FormDropdown from "../form-dropdown";
import MultiSelectDropdown from "../MultiSelectDropdown";
import PhoneCountrySelect from "../phone-country-select";
import { uploadFile, selectFile } from "../../utils/fileUploadUtils";
import { ProfileImage } from "../ProfileImage";
import PhoneInput, {
    isValidPhoneNumber,
    isPossiblePhoneNumber,
    parsePhoneNumber,
    type Country,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

interface MemberFormData {
    firstName: string;
    middleName: string;
    lastName: string;
    designation: string;
    department: string;
    email: string;
    contactNumber: string;
    groups: string[];
    gender: string;
}

interface CreateMemberFormProps {
    memberId?: number;
    fixedCompanyId?: number;
    redirectOnSuccess?: boolean;
    createButtonLabel?: string;
}

const CreateMemberForm: React.FC<CreateMemberFormProps> = ({
    memberId,
    fixedCompanyId,
    redirectOnSuccess = true,
    createButtonLabel = "Create Member",
}) => {
    const getPhoneValidationCountry = (
        value: string,
        country?: Country,
    ): Country | undefined => {
        if (country) return country;
        if (!value.trim()) return undefined;
        try {
            return parsePhoneNumber(value)?.country;
        } catch {
            return undefined;
        }
    };

    const showInvalidPhoneHint = (value: string, country?: Country): boolean => {
        const trimmed = value.trim();
        if (!trimmed) return false;

        const validationCountry = getPhoneValidationCountry(trimmed, country);
        if (!validationCountry) return false;

        return (
            isPossiblePhoneNumber(trimmed, validationCountry) &&
            !isValidPhoneNumber(trimmed, validationCountry)
        );
    };

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const accessToken = useAppSelector((state) => state.auth.accessToken);
    const user = useAppSelector((state) => state.auth.user);
    const isEditMode = !!memberId;

    const departments = useAppSelector((state) => state.filters.departments);
    const departmentsStatus = useAppSelector((state) => state.filters.departmentsStatus);
    const departmentsError = useAppSelector((state) => state.filters.departmentsError);

    const [roleOptions, setRoleOptions] = useState<MemberRoleOption[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesError, setRolesError] = useState<string | null>(null);

    const [formData, setFormData] = useState<MemberFormData>({
        firstName: "",
        middleName: "",
        lastName: "",
        designation: "",
        department: "",
        email: "",
        contactNumber: "",
        groups: [],
        gender: "",
    });
    const [companyId, setCompanyId] = useState<number | null>(null);
    const [profilePictureId, setProfilePictureId] = useState<number | null>(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [phoneCountry, setPhoneCountry] = useState<Country | undefined>(undefined);
    const [showPhoneCountryError, setShowPhoneCountryError] = useState(false);

    useEffect(() => {
        dispatch(fetchFiltersDepartments());

        // Fetch available roles for member from API
        const loadRoles = async () => {
            try {
                setRolesLoading(true);
                setRolesError(null);
                const result = await fetchMemberRoles();
                if (result.error) {
                    setRolesError(result.error);
                } else if (result.data) {
                    setRoleOptions(result.data);
                }
            } catch (error) {
                setRolesError(
                    error instanceof Error
                        ? error.message
                        : "Failed to load roles. Please try again."
                );
            } finally {
                setRolesLoading(false);
            }
        };

        loadRoles();
    }, [dispatch]);

    // Clear form when entering create mode (not edit mode) or when memberId changes
    useEffect(() => {
        if (!isEditMode && !memberId) {
            // Reset form to initial empty state
            setFormData({
                firstName: "",
                middleName: "",
                lastName: "",
                designation: "",
                department: "",
                email: "",
                contactNumber: "",
                groups: [],
                gender: "",
            });
            // Clear profile picture
            setProfilePictureId(null);
            if (profilePicturePreview) {
                URL.revokeObjectURL(profilePicturePreview);
            }
            setProfilePicturePreview(null);
            // Clear error and success messages
            setSaveError(null);
            setSaveSuccess(false);
            // Reset loading state
            setIsLoading(false);
        }
    }, [isEditMode, memberId, profilePicturePreview]);

    // Cleanup preview URL on unmount
    useEffect(() => {
        return () => {
            if (profilePicturePreview) {
                URL.revokeObjectURL(profilePicturePreview);
            }
        };
    }, [profilePicturePreview]);

    // Fetch company ID
    useEffect(() => {
        if (fixedCompanyId) {
            setCompanyId(fixedCompanyId);
            return;
        }

        if (!accessToken) return;

        const loadCompanyId = async () => {
            try {
                let companyMemberId = user?.company_member_id;

                if (!companyMemberId) {
                    const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
                    companyMemberId = ensuredUser.company_member_id as number | undefined;
                }

                if (companyMemberId) {
                    const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken);
                    if (companyMemberResponse.data?.company?.id) {
                        setCompanyId(companyMemberResponse.data.company.id);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch company data:", error);
            }
        };

        loadCompanyId();
    }, [fixedCompanyId, user?.company_member_id, accessToken, dispatch]);

    // Fetch member data when in edit mode
    useEffect(() => {
        if (isEditMode && memberId && accessToken) {
            const loadMemberData = async () => {
                setIsLoading(true);
                try {
                    const result = await fetchCompanyMemberById(memberId);
                    if (result.error) {
                        setSaveError(result.error);
                        setIsLoading(false);
                        return;
                    }

                    if (result.data) {
                        const memberData = result.data;
                        const contactNumber = memberData.contact_number || "";
                        let resolvedPhoneCountry: Country | undefined;
                        if (contactNumber) {
                            try {
                                resolvedPhoneCountry = parsePhoneNumber(contactNumber)?.country;
                            } catch {
                                resolvedPhoneCountry = undefined;
                            }
                        }

                        setFormData({
                            firstName: memberData.first_name || "",
                            middleName: memberData.middle_name || "",
                            lastName: memberData.last_name || "",
                            designation: memberData.designation || "",
                            department: memberData.department?.name || "",
                            email: memberData.email || "",
                            contactNumber,
                            groups: memberData.groups && memberData.groups.length > 0
                                ? memberData.groups
                                : (memberData.group ? [memberData.group] : []),
                            gender: memberData.gender || "",
                        });
                        setPhoneCountry(resolvedPhoneCountry);
                        setShowPhoneCountryError(false);
                        // Normalize and set existing profile picture ID if available
                        if (memberData.profile_picture) {
                            if (typeof memberData.profile_picture === "number") {
                                setProfilePictureId(memberData.profile_picture);
                            } else if (
                                typeof memberData.profile_picture === "object" &&
                                "id" in memberData.profile_picture
                            ) {
                                setProfilePictureId(
                                    (memberData.profile_picture as { id: number }).id
                                );
                            }
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch member data:", error);
                    setSaveError("Failed to load member data. Please try again.");
                } finally {
                    setIsLoading(false);
                }
            };

            loadMemberData();
        }
    }, [isEditMode, memberId, accessToken]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleImageUpload = async () => {
        if (!accessToken) {
            setSaveError("Authentication required to upload image");
            return;
        }

        try {
            // Open file picker
            const file = await selectFile("image/*");
            if (!file) {
                return; // User cancelled
            }

            // Validate file type
            if (!file.type.startsWith("image/")) {
                setSaveError("Please select a valid image file");
                return;
            }

            // Validate file size (e.g., max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                setSaveError("Image size must be less than 5MB");
                return;
            }

            setIsUploading(true);
            setSaveError(null);

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setProfilePicturePreview(previewUrl);

            console.log("ImageFile: ", file);

            // Upload file
            const fileId = await uploadFile(file, accessToken);

            if (fileId) {
                setProfilePictureId(fileId);
                toast.success("Image uploaded successfully");
            } else {
                setSaveError("Failed to upload image. Please try again.");
                setProfilePicturePreview(null);
                toast.error("Failed to upload image. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            setSaveError("Failed to upload image. Please try again.");
            setProfilePicturePreview(null);
            toast.error("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const isFormValid = () => {
        if (isEditMode) {
            // For edit mode: firstName, lastName, designation, department, groups are required
            return (
                formData.firstName.trim() !== "" &&
                formData.lastName.trim() !== "" &&
                formData.designation.trim() !== "" &&
                formData.department !== "" &&
                formData.groups.length > 0
            );
        } else {
            // For create mode: firstName, lastName, email, designation, department, groups are required
            return (
                formData.firstName.trim() !== "" &&
                formData.lastName.trim() !== "" &&
                formData.email.trim() !== "" &&
                formData.designation.trim() !== "" &&
                formData.department !== "" &&
                formData.groups.length > 0 &&
                companyId !== null &&
                phoneCountry !== undefined &&
                formData.contactNumber.trim() !== "" &&
                isValidPhoneNumber(formData.contactNumber) &&
                !showInvalidPhoneHint(formData.contactNumber, phoneCountry)
            );
        }
    };

    const handleSave = async () => {
        if (!accessToken) {
            setSaveError("Authentication required");
            return;
        }

        if (isEditMode) {
            // For edit mode, only validate required fields for update (middleName is optional)
            if (!formData.firstName || !formData.lastName || !formData.designation || !formData.department || formData.groups.length === 0) {
                setSaveError("Please fill in all required fields");
                return;
            }
        } else {
            // For create mode, validate all fields including email and groups (middleName is optional)
            if (!formData.firstName || !formData.lastName || !formData.email || !formData.designation || formData.groups.length === 0 || !formData.department) {
                setSaveError("Please fill in all required fields");
                return;
            }

            if (!companyId) {
                setSaveError("Company information is missing. Cannot create member.");
                return;
            }
        }

        if (!isEditMode && !phoneCountry) {
            setShowPhoneCountryError(true);
            setSaveError("Please select a country for the contact number");
            return;
        }

        if (
            !isEditMode &&
            (!formData.contactNumber.trim() || !isValidPhoneNumber(formData.contactNumber))
        ) {
            setSaveError("Please enter a valid contact number");
            return;
        }

        if (showInvalidPhoneHint(formData.contactNumber, phoneCountry)) {
            setSaveError("Please enter a valid contact number");
            return;
        }

        try {
            setIsSaving(true);
            setSaveError(null);
            setSaveSuccess(false);

            const selectedDept = departments.find(
                (dept) => dept.name === formData.department,
            );

            if (!selectedDept) {
                setSaveError("Please select a valid department");
                setIsSaving(false);
                return;
            }

            const primaryGroup = formData.groups[0] || null;

            if (isEditMode && memberId) {
                // Update existing member
                const payload: UpdateCompanyMemberPayload = {
                    first_name: formData.firstName,
                    middle_name: formData.middleName || "",
                    last_name: formData.lastName,
                    designation: formData.designation,
                    gender: formData.gender || "M",
                    department: selectedDept.id,
                    contact_number: formData.contactNumber || null,
                    profile_picture: profilePictureId || null,
                    groups: formData.groups,
                    ...(primaryGroup ? { group: primaryGroup as UpdateCompanyMemberPayload["group"] } : {}),
                };

                const result = await updateCompanyMember(memberId, payload);

                if (result.error) {
                    setSaveError(result.error);
                    toast.error(result.error);
                } else {
                    setSaveSuccess(true);
                    toast.success("Member updated successfully");
                    // Redirect to all members page after successful update
                    setTimeout(() => {
                        navigate("/all-members");
                    }, 1500);
                }
            } else {
                // Create new member
                const payload: CreateCompanyMemberPayload = {
                    groups: formData.groups,
                    ...(primaryGroup ? { group: primaryGroup as CreateCompanyMemberPayload["group"] } : {}),
                    email: formData.email,
                    first_name: formData.firstName,
                    middle_name: formData.middleName || "",
                    last_name: formData.lastName,
                    designation: formData.designation,
                    gender: "M",
                    company: companyId!,
                    department: selectedDept.id,
                    contact_number: formData.contactNumber || null,
                    profile_picture: profilePictureId || null,
                };

                const result = await createCompanyMember(payload);

                if (result.error) {
                    setSaveError(result.error);
                    toast.error(result.error);
                } else {
                    setSaveSuccess(true);
                    toast.success("Member created successfully");
                    if (redirectOnSuccess) {
                        // Redirect to all members page after successful creation
                        setTimeout(() => {
                            navigate("/all-members");
                        }, 1500);
                    } else {
                        setFormData({
                            firstName: "",
                            middleName: "",
                            lastName: "",
                            designation: "",
                            department: "",
                            email: "",
                            contactNumber: "",
                            groups: [],
                            gender: "",
                        });
                        setPhoneCountry(undefined);
                        setShowPhoneCountryError(false);
                        setProfilePictureId(null);
                        if (profilePicturePreview) {
                            URL.revokeObjectURL(profilePicturePreview);
                        }
                        setProfilePicturePreview(null);
                    }
                }
            }
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : isEditMode
                        ? "Failed to update member. Please try again."
                        : "Failed to create member. Please try again.";
            setSaveError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Profile Photo Section */}
            <div className="flex items-center gap-4 mb-8">
                <div className="relative">
                    <div className="w-24 h-24 bg-gray-400 rounded-full border-2 border-black flex items-center justify-center overflow-hidden">
                        {profilePicturePreview ? (
                            <img
                                src={profilePicturePreview}
                                alt="Profile preview"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        ) : profilePictureId ? (
                            <ProfileImage
                                profilePicture={profilePictureId}
                                accessToken={accessToken || undefined}
                                alt="Profile"
                                className="w-24 h-24 rounded-full object-cover"
                                fallbackPath="/assets/images/Avatar2.jpg"
                            />
                        ) : (
                            <img
                                src="/assets/images/user-Avatar.png"
                                alt="Profile placeholder"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleImageUpload}
                        disabled={isUploading || !accessToken}
                        className="absolute bottom-0 right-0 bg-[#ABD405] p-2 rounded-full border-2 border-black cursor-pointer hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Upload Photo"
                    >
                        {isUploading ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-md animate-spin" />
                        ) : (
                            <Camera size={16} className="text-[#000000]" />
                        )}
                    </button>
                </div>
                <div>
                    <p className="font-medium text-[#000000]">Upload Photo</p>
                    {isUploading && (
                        <p className="text-sm text-gray-500">Uploading...</p>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded-md">
                    Loading member data...
                </div>
            )}

            {/* Success Message */}
            {saveSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md">
                    Member {isEditMode ? 'updated' : 'created'} successfully!{(!isEditMode && redirectOnSuccess) ? " Redirecting..." : ""}
                </div>
            )}

            {/* Error Message */}
            {saveError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md">
                    {saveError}
                </div>
            )}

            {/* Form Fields Grid */}
            <div className="grid grid-cols-3 gap-6">
                {/* First Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        First name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3.5 font-normal rounded-md bg-[#F9F9F9] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.firstName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter first name"
                        autoComplete="off"
                        required
                    />
                </div>

                {/* Middle Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Middle name
                    </label>
                    <input
                        type="text"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3.5 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.middleName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter middle name"
                        autoComplete="off"
                        required
                    />
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3.5 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.lastName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter last name"
                        autoComplete="off"
                        required
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {/* Designation */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Designation <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3.5 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.designation ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter designation"
                        autoComplete="off"
                        required
                    />
                </div>

                {/* Department */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Department <span className="text-red-500">*</span>
                    </label>
                    {departmentsStatus === "loading" ? (
                        <div className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-400">
                            Loading departments...
                        </div>
                    ) : departmentsStatus === "failed" ? (
                        <div className="text-red-500 text-sm">{departmentsError || "Failed to load departments."}</div>
                    ) : (
                        <FormDropdown
                            value={formData.department}
                            onSelect={(value) => setFormData((prev) => ({ ...prev, department: value }))}
                            placeholder="Select Department"
                            options={departments.map((option) => ({
                                label: option.name || "",
                                value: option.name || "",
                            }))}
                        />
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {/* Email Address */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Email address {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={isEditMode}
                        className={`w-full px-4 py-3.5 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.email ? "text-gray-800" : "text-gray-400"} ${isEditMode ? "opacity-60 cursor-not-allowed" : ""}`}
                        placeholder="Enter email address"
                        autoComplete="off"
                        required={!isEditMode}
                    />
                </div>

                {/* Contact Number */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Contact number {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {/* <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3.5 rounded-md bg-[#F9F9F9] font-normal text-gray-400 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                        autoComplete="off"
                    /> */}

                    <PhoneInput
                        international
                        country={phoneCountry}
                        onCountryChange={(country) => {
                            setPhoneCountry(country);
                            if (country) {
                                setShowPhoneCountryError(false);
                            }
                        }}
                        countrySelectComponent={PhoneCountrySelect}
                        value={formData.contactNumber}
                        onChange={(val) => setFormData(prev => ({
                            ...prev, contactNumber: val ?? ''
                        }))}
                        className={`phone-input-wrapper ${formData.contactNumber ? "text-gray-800" : "text-gray-400"}`}
                    />
                    {showPhoneCountryError && !phoneCountry && (
                        <div className="text-red-500 text-sm">Please select a country</div>
                    )}
                    {showInvalidPhoneHint(formData.contactNumber, phoneCountry) && (
                        <div className="text-red-500 text-sm">Please enter a valid phone number</div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {/* Role */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Roles {!isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {rolesLoading ? (
                        <div className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-400">
                            Loading roles...
                        </div>
                    ) : rolesError ? (
                        <div className="text-red-500 text-sm">
                            {rolesError}
                        </div>
                    ) : (
                        <MultiSelectDropdown
                            options={roleOptions}
                            selected={formData.groups}
                            onToggle={(val) =>
                                setFormData((prev) => {
                                    const exists = prev.groups.includes(val);
                                    return {
                                        ...prev,
                                        groups: exists ? prev.groups.filter((g) => g !== val) : [...prev.groups, val],
                                    };
                                })
                            }
                            placeholder="Select roles"
                        />
                    )}
                </div>

            </div>

            {/* Save Button */}
            <div className="flex justify-end items-center pt-4">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving || isLoading || !isFormValid()}
                    className="btn-cta px-8 py-2 font-semibold rounded-md disabled:opacity-60"
                >
                    {isSaving
                        ? (isEditMode ? "Updating..." : "Creating...")
                        : (isEditMode ? "Update Member" : createButtonLabel)
                    }
                </button>
            </div>
        </div>
    );
};

export default CreateMemberForm;

